import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '@/api/endpoints/auth';
import { track } from '@/analytics/tracker';
import { useAuth } from '@/auth/AuthProvider';
import { usePaywall } from '@/billing/PaywallContext';
import { appConfig, storageKeys } from '@/config/app';
import { useCanvases } from '@/library/useCanvases';
import { local } from '@/lib/storage';
import { onboardingSteps, type OnboardingStep } from './steps';
import { TourOverlay } from './TourOverlay';
import { hasCatalogQuery, shouldAutoStartTour } from './tourRules';

export type TourSource = 'auto' | 'menu' | 'dev';

interface TourContextValue {
  active: boolean;
  startTour: (source: TourSource) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

/** Пауза после навигации/старта — даём странице отрисоваться перед замером цели. */
const START_DELAY_MS = 500;

function routePath(route: OnboardingStep['route'], firstCanvasId: number | null): string {
  switch (route) {
    case 'saved':
      return '/saved';
    case 'canvases':
      return '/canvases';
    case 'canvas-first':
      return firstCanvasId ? `/canvases/${firstCanvasId}` : '/canvases';
    default:
      return '/';
  }
}

/**
 * Онбординг-тур (ONB-1…7, ONB-10): автопоказ новому пользователю в каталоге,
 * повтор из меню, dev-предпросмотр `?onboarding=1`. Источник истины — сервер:
 * показ и завершение/пропуск пишутся `POST /me/onboarding-complete`.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isAccessResolved, patchUser } = useAuth();
  const { intent: paywallIntent } = usePaywall();
  const { data: canvases } = useCanvases();
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<{ source: TourSource } | null>(null);
  // Заход с готовым запросом (`/app/?q=…` из статьи блога) читается один раз при монтировании:
  // гид не стартует поверх выдачи в этой сессии страницы и не стирает адрес, а очистка поиска
  // позже не запускает его посреди работы. Серверный флаг не ставится — гид покажется при
  // следующем заходе в чистый каталог.
  const [enteredWithQuery] = useState(() => hasCatalogQuery(location.search));
  const autoTried = useRef(false);
  const marking = useRef(false);
  const firstCanvasId = canvases?.[0]?.id ?? null;

  const markDone = useCallback(
    async (source: TourSource) => {
      if (source === 'dev') return; // ONB-10: предпросмотр сервер не помечает
      local.set(storageKeys.onboardingDone, '1');
      if (marking.current || user?.onboarding_completed_at) return;
      marking.current = true;
      try {
        const result = await authApi.completeOnboarding();
        patchUser({ onboarding_completed_at: result.onboarding_completed_at });
      } catch {
        marking.current = false; // повторим при завершении/пропуске
      }
    },
    [patchUser, user?.onboarding_completed_at],
  );

  const startTour = useCallback(
    (source: TourSource) => {
      track('onboarding_shown', { metadata: { source } });
      // Тур начинается в каталоге; `?video=` и прочие слои закрываются сменой адреса.
      if (location.pathname !== '/' || location.search)
        navigate('/', { replace: source !== 'menu' });
      setSession({ source });
      if (source === 'auto') void markDone(source); // ONB-3: показ помечается сразу
    },
    [location.pathname, location.search, navigate, markDone],
  );

  // ONB-10: `?onboarding=1` — только в dev-сборке; startTour сам уводит на чистый `/`.
  useEffect(() => {
    if (!import.meta.env.DEV || !isAuthenticated) return;
    if (new URLSearchParams(location.search).get('onboarding') !== '1') return;
    startTour('dev');
  }, [location.search, isAuthenticated, startTour]);

  // ONB-1: автопоказ один раз за сессию страницы.
  useEffect(() => {
    if (session || autoTried.current) return;
    const ok = shouldAutoStartTour({
      user,
      isAuthenticated,
      isAccessResolved,
      pathname: location.pathname,
      localDone: local.get(storageKeys.onboardingDone) === '1',
      blocked: paywallIntent !== null,
      enteredWithQuery,
    });
    if (!ok) return;
    autoTried.current = true;
    const t = setTimeout(() => startTour('auto'), START_DELAY_MS);
    return () => clearTimeout(t);
  }, [
    session,
    user,
    isAuthenticated,
    isAccessResolved,
    location.pathname,
    paywallIntent,
    enteredWithQuery,
    startTour,
  ]);

  // Выход из аккаунта закрывает тур.
  useEffect(() => {
    if (!isAuthenticated) setSession(null);
  }, [isAuthenticated]);

  const goToStep = useCallback(
    (step: OnboardingStep) => {
      const path = routePath(step.route, firstCanvasId);
      if (path === location.pathname && !location.search) return false;
      navigate(path, { replace: true });
      return true;
    },
    [firstCanvasId, location.pathname, location.search, navigate],
  );

  const finish = useCallback(
    (reason: 'completed' | 'skipped', stepIndex: number) => {
      const source = session?.source ?? 'menu';
      const step = onboardingSteps[stepIndex];
      if (reason === 'completed') {
        track('onboarding_completed', {
          metadata: { steps_total: onboardingSteps.length, source },
        });
      } else {
        track('onboarding_skipped', {
          metadata: { at_step: stepIndex + 1, step_key: step?.key ?? null, source },
        });
      }
      setSession(null);
      void markDone(source);
      if (location.pathname !== '/') navigate('/', { replace: true });
      if (reason === 'completed') focusSearch();
    },
    [session, markDone, location.pathname, navigate],
  );

  const value = useMemo(() => ({ active: session !== null, startTour }), [session, startTour]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {session ? (
        <TourOverlay steps={onboardingSteps} onNavigate={goToStep} onFinish={finish} />
      ) : null}
    </TourContext.Provider>
  );
}

/** ONB-6: фокус в строку поиска с подсветкой; событие — только если фокус действительно встал. */
function focusSearch(attempt = 0): void {
  const input = document.querySelector<HTMLInputElement>('[data-tour="search"]');
  if (input && input.offsetParent !== null) {
    input.focus();
    if (document.activeElement === input) {
      input.setAttribute('data-tour-highlight', '');
      setTimeout(() => input.removeAttribute('data-tour-highlight'), 1800);
      track('onboarding_search_focused');
    }
    return;
  }
  if (attempt < 20) setTimeout(() => focusSearch(attempt + 1), 100);
}

export function useTour(): TourContextValue {
  const value = useContext(TourContext);
  if (!value) throw new Error('useTour must be used inside <TourProvider>');
  return value;
}

/** В mock-сборке подсказка в консоль, как открыть предпросмотр. */
if (import.meta.env.DEV && appConfig.apiMode === 'mock') {
  // eslint-disable-next-line no-console
  console.info('[onboarding] preview: add ?onboarding=1 to the catalog URL');
}
